import {Col, Hidden, media, Row, Spacer, spacing} from '@nfq/react-grid';
import Link from 'next/link';
import {useTranslation} from 'next-i18next';
import styled from 'styled-components';

import {Newsletter} from 'UI/components/action/ultra/Newsletter/Newsletter';
import {Container} from 'UI/components/layout/ultra/Container';
import {Text} from 'UI/components/layout/ultra/Text';

import {UltraFooter} from 'UI/layouts/ultra/UltraFooter';
import {UltraHeader} from 'UI/layouts/ultra/UltraHeader';
import {EquipmentSection} from 'UI/modules/ultra/sections/details/EquipmentSection';
import {OverviewSection} from 'UI/modules/ultra/sections/details/OverviewSection';
import {SpecSection} from 'UI/modules/ultra/sections/details/SpecSection';

import {ArrowLeftSmall} from 'Images/icons/ultra';

/**
 * React component for displaying the CCT EVO ULTRA details.
 * This component renders the header, overview section, equipment section, and specification section of the ultra details.
 *
 * @returns JSX element representing the ultra details.
 *
 * @example
 * ```tsx
 * <UltraDetails />
 * ```
 */
export const UltraDetails = () => {
    const {t} = useTranslation('ultra', {keyPrefix: 'details'});

    return (
        <>
            <UltraHeader />
            <Section>
                <Container>
                    <Row>
                        <Col>
                            <nav>
                                <StyledLink href="/">
                                    <ArrowLeftSmall /> <Hidden md sm xs><span>{t('back')}</span></Hidden>
                                </StyledLink>
                            </nav>
                        </Col>
                    </Row>
                    <Spacer y={{lg: 12, xs: 6}} />
                    <OverviewSection />
                    <Spacer y={{lg: 60, xs: 20}} isNotStretching />
                    <EquipmentSection />
                    <Spacer y={{lg: 60, xs: 20}} isNotStretching />
                    <SpecSection />
                    <Spacer y={{lg: 30, xs: 2}} isNotStretching />
                    <Row justify="center">
                        <Col lg={10}>
                            <Text $size="xs">{t('disclaimer')}</Text>
                        </Col>
                    </Row>
                </Container>
            </Section>
            <Newsletter />
            <UltraFooter />
        </>
    );
};

const Section = styled.section`
    background: linear-gradient(270deg, #2D2C2D 0%, #000000 100%);
    padding-block: ${spacing(4)} ${spacing(20)};
    padding-inline: ${spacing(4)};
    position: relative;

    ${media('md')} {
        padding-block: ${spacing(12)} ${spacing(60)};
        padding-inline: ${spacing(15)};
    }
`;

const StyledLink = styled(Link)`
    align-items: center;
    border-radius: 4px;
    color: ${({theme}) => theme.colors.ultraPageForeground};
    display: inline-flex;
    font: 2rem / 1.5 ${({theme}) => theme.fonts.Dinot};
    gap: 10px;
    text-decoration: none;

    &:focus-visible {
        outline: 1px ${({theme}) => theme.colors.ultraFocusColor} solid;
        outline-offset: 8px;
    }
`;